import { useCallback, useEffect, useState } from "react";
import api from "../services/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isIosDevice() {
  if (typeof window === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function canUseWebPush() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

const withTimeout = (promise, ms, message) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(message)), ms)
    )
  ]);

async function ensureServiceWorkerRegistration() {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;

  const swUrl = `${process.env.PUBLIC_URL || ""}/service-worker.js`;
  return navigator.serviceWorker.register(swUrl);
}

const useWebPush = () => {
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [supported] = useState(() => canUseWebPush());

  const refreshSubscriptionState = useCallback(async () => {
    if (!supported) return;
    try {
      await ensureServiceWorkerRegistration();
      const registration = await withTimeout(
        navigator.serviceWorker.ready,
        8000,
        "Service worker não ficou pronto a tempo"
      );
      const sub = await registration.pushManager.getSubscription();
      setSubscribed(Boolean(sub));
      setPermission(Notification.permission);
    } catch (_) {
      setSubscribed(false);
    }
  }, [supported]);

  useEffect(() => {
    refreshSubscriptionState();
  }, [refreshSubscriptionState]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMessage = event => {
      const data = event.data || {};
      if (data.type === "HEIDI_PUSH_NAVIGATE" && data.url) {
        try {
          const path = data.url.replace(window.location.origin, "");
          if (path && path !== window.location.pathname) {
            window.location.assign(data.url);
          }
        } catch (_) {
          /* ignore */
        }
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () =>
      navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) {
      throw new Error("Web Push não suportado neste navegador");
    }
    setLoading(true);
    setError(null);
    try {
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      if (permissionResult !== "granted") {
        throw new Error("Permissão de notificação negada");
      }

      const { data } = await api.get("/push/vapidPublicKey");
      await ensureServiceWorkerRegistration();
      const registration = await withTimeout(
        navigator.serviceWorker.ready,
        10000,
        "Service worker não ficou pronto a tempo"
      );
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey)
        });
      }

      const json = subscription.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        userAgent: navigator.userAgent
      });
      setSubscribed(true);
      return subscription;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      const registration = await withTimeout(
        navigator.serviceWorker.ready,
        8000,
        "Service worker não ficou pronto a tempo"
      );
      const subscription = await registration.pushManager.getSubscription();
      const endpoint = subscription?.endpoint;
      if (endpoint) {
        await api.delete("/push/subscribe", { data: { endpoint } });
      }
      if (subscription) {
        await subscription.unsubscribe();
      }
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    const { data } = await api.post("/push/test");
    return data;
  }, []);

  return {
    supported,
    permission,
    subscribed,
    loading,
    error,
    isIos: isIosDevice(),
    isStandalone: isStandalonePwa(),
    subscribe,
    unsubscribe,
    sendTest,
    refreshSubscriptionState
  };
};

export default useWebPush;
